import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMatch, useNavigate } from "react-router-dom";
import {
  GSSidePanelPage,
  GSActionBar,
  GSEmptyList,
  GSInput,
  GSItemList,
  GSButton,
  GSInfoGroup,
  defaultGapping,
  defaultLayouts,
  defaultPadding,
  defaultTypography,
  golfstatusColors,
  golfstatusSpacing,
} from "golfstatus_react_components";
import {
  faChevronLeft,
  faSearch,
  faPlus,
  faCircleCheck,
  faCircleNotch,
} from "@fortawesome/free-solid-svg-icons";
import {
  getTournamentForms,
  selectTournamentForms,
  selectLoading as selectPackageLoading,
} from "../../../reducers/packageSlice";
import {
  slideoutContentStyle,
  slideoutPageStyle,
  slideoutRootStyle,
} from "../../../templates/slideout-styles";
import {
  getRecordCustomFieldResponses,
  attachRecordForms,
  clearNotifications,
  clearCurrentRecordResponses,
  selectCurrentRecordResponses,
  selectCustomFieldResponsesNotifications,
  selectCustomFieldResponsesLoading,
} from "../../../reducers/customFieldResponsesSlice";
import { useNotificationBanner } from "../../../hooks/notificationHooks";
import { getDate } from "../../../helpers/Dates";

const sectionStyle = {
  ...defaultLayouts.vStack,
  ...defaultLayouts.fullWidth,
  ...defaultGapping.mediumGap,
  ...defaultPadding.xLargePad.apply("horizontal"),
  ...defaultPadding.xLargePad.apply("vertical"),
  borderBottom: `1px solid ${golfstatusColors.grey100}`,
};

const searchSectionStyle = {
  ...sectionStyle,
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: golfstatusColors.white,
};

const bodySectionStyle = {
  ...defaultLayouts.vStack,
  ...defaultLayouts.fullWidth,
};


const itemWrapperStyle = {
  ...defaultLayouts.fullWidth,
  ...defaultPadding.xLargePad.apply("horizontal"),
  ...defaultPadding.xLargePad.apply("vertical"),
  borderBottom: `1px solid ${golfstatusColors.grey100}`,
};

const headlineStyle = { ...defaultTypography.headline3 };

const formNameStyle = { ...defaultTypography.body, whiteSpace: "normal" };

const createdAtStyle = {
  ...defaultTypography.bodyRegular,
  color: golfstatusColors.grey800,
  opacity: 0.7,
};

const addedPillStyle = {
  opacity: 1,
  paddingTop: golfstatusSpacing.mediumPad,
};

const listStyle = { ...defaultLayouts.fullWidth };

const emptyListStyle = { ...defaultPadding.smallLayoutPad.apply("vertical") };

const collectAttachedIds = (payload) => {
  const ids = new Set();
  (payload?.items ?? []).forEach((item) => {
    const id = item?.question?.registrationFieldSet?.id;
    if (id && item?.response != null) {
      ids.add(id);
    }
  });
  return ids;
};

const RecordFormsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const teamPlayerMatch = useMatch(
    "/:tournamentID/teams/:teamID/players/:playerID/form-responses/forms"
  );
  const teamMatch = useMatch("/:tournamentID/teams/:teamID/form-responses/forms");
  const unassignedMatch = useMatch(
    "/:tournamentID/teams/unassigned-players/:playerID/form-responses/forms"
  );
  const sponsorMatch = useMatch(
    "/:tournamentID/sponsors/:sponsorID/form-responses/forms"
  );
  const match =
    teamPlayerMatch ?? teamMatch ?? unassignedMatch ?? sponsorMatch;
  const tournamentID = match?.params?.tournamentID;

  let recordType = "tournament-teams";
  let recordKey = teamMatch?.params?.teamID;
  if (sponsorMatch) {
    recordType = "sponsors";
    recordKey = sponsorMatch.params.sponsorID;
  } else if (teamPlayerMatch) {
    recordType = "players";
    recordKey = teamPlayerMatch.params.playerID;
  } else if (unassignedMatch) {
    recordType = "players";
    recordKey = unassignedMatch.params.playerID;
  }

  const tournamentForms = useSelector(selectTournamentForms);
  const payload = useSelector(selectCurrentRecordResponses);
  const notifications = useSelector(selectCustomFieldResponsesNotifications);
  const packageLoading = useSelector(selectPackageLoading);
  const recordLoading = useSelector(selectCustomFieldResponsesLoading);

  const [search, setSearch] = useState("");
  const [addingId, setAddingId] = useState(null);
  const [banner] = useNotificationBanner({
    notifications,
    timeoutAction: () => dispatch(clearNotifications()),
  });

  useEffect(() => {
    if (tournamentID) {
      dispatch(getTournamentForms(tournamentID));
    }
  }, [dispatch, tournamentID]);

  useEffect(() => {
    if (recordType && recordKey) {
      dispatch(clearCurrentRecordResponses());
      dispatch(getRecordCustomFieldResponses({ recordType, key: recordKey }));
    }
    return () => {
      dispatch(clearNotifications());
    };
  }, [dispatch, recordType, recordKey]);

  const attachedIds = collectAttachedIds(payload);
  const recordName = payload?.record?.name;

  const addForm = async (form) => {
    setAddingId(form?.id);
    const result = await dispatch(
      attachRecordForms({
        recordType,
        key: recordKey,
        registrationFieldSets: [form?.id],
      })
    );
    if (result?.meta?.requestStatus === "fulfilled") {
      navigate(-1);
    } else {
      setAddingId(null);
    }
  };

  const filteredForms = (tournamentForms ?? [])
    .filter((form) =>
      form?.name?.toLowerCase?.().includes(search.toLowerCase())
    )
    .sort((a, b) =>
      `${b?.createdAt ?? ""}`.localeCompare(`${a?.createdAt ?? ""}`)
    );

  const getNavigation = () => ({
    title: "Forms",
    leftIcon: faChevronLeft,
    leftButtonClick: () => navigate(-1),
  });

  const getHeaderSection = () => (
    <div style={sectionStyle}>
      <GSActionBar
        header={
          <div style={headlineStyle}>
            {recordName ? `${recordName} Forms` : "Forms"}
          </div>
        }
      ></GSActionBar>
    </div>
  );

  const getSearchSection = () => (
    <div style={searchSectionStyle}>
      <GSInput
        textValue={search}
        placeholder="Search..."
        leftIcon={faSearch}
        onChange={(event) => setSearch(event.target.value)}
      />
    </div>
  );

  const getRightAction = (form) => {
    if (addingId === form?.id) {
      return (
        <GSButton
          type="grey no-wrap"
          title="Adding..."
          buttonIcon={faCircleNotch}
          isDisabled={true}
        />
      );
    }
    if (attachedIds.has(form?.id)) {
      return null;
    }
    const hasQuestions = (form?.activeCustomFieldCount ?? 0) > 0;
    return (
      <GSButton
        type="grey no-wrap"
        title="Add Form"
        buttonIcon={faPlus}
        isDisabled={!hasQuestions}
        onClick={() => addForm(form)}
      />
    );
  };

  const getItemGroups = (form) => {
    const leftItems = [
      {
        type: "primary no-wrap full-width",
        value: form?.name,
        sectionItemStyle: formNameStyle,
      },
      {
        type: "secondary",
        value: form?.createdAt ? `Created on ${getDate(form.createdAt)}` : "",
        sectionItemStyle: createdAtStyle,
      },
    ];
    if (attachedIds.has(form?.id)) {
      leftItems.push({
        type: "secondary no-wrap",
        value: (
          <GSButton
            type="status secondary pill green no-wrap"
            title="Added"
            buttonIcon={faCircleCheck}
          />
        ),
        sectionItemStyle: addedPillStyle,
      });
    }
    const groups = [
      {
        fit: "stretch",
        sections: [{ sectionItems: leftItems }],
      },
    ];
    const rightAction = getRightAction(form);
    if (rightAction) {
      groups.push({
        sections: [
          { sectionItems: [{ type: "primary no-wrap", value: rightAction }] },
        ],
      });
    }
    return groups;
  };

  const getBodySection = () => (
    <div style={bodySectionStyle}>
      <GSItemList
        type="vertical"
        style={listStyle}
        items={filteredForms}
        listItem={(form) => (
          <div style={itemWrapperStyle}>
            <GSInfoGroup dataGroups={getItemGroups(form)} />
          </div>
        )}
        emptyMessage={
          <GSEmptyList
            style={emptyListStyle}
            title={
              search
                ? "We couldn't find the form you're looking for..."
                : "No Forms Found"
            }
            detail={
              search
                ? "Try changing the search term."
                : "There are no forms for this tournament."
            }
            actions={
              search
                ? [
                    {
                      title: "Clear Search",
                      type: "grey",
                      onClick: () => setSearch(""),
                    },
                  ]
                : []
            }
          />
        }
      ></GSItemList>
    </div>
  );

  const getContent = () => (
    <div style={slideoutContentStyle}>
      {getHeaderSection()}
      {getSearchSection()}
      {getBodySection()}
    </div>
  );

  return (
    <tournament-record-forms style={slideoutRootStyle}>
      <GSSidePanelPage
        header={getNavigation()}
        loading={
          (packageLoading?.includes?.("getTournamentForms") &&
            (tournamentForms?.length ?? 0) === 0) ||
          (recordLoading?.includes?.("getRecordCustomFieldResponses") &&
            !payload?.record)
        }
        banner={banner}
        content={getContent()}
        style={slideoutPageStyle}
      ></GSSidePanelPage>
    </tournament-record-forms>
  );
};

export default RecordFormsPage;
